import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { getUserData } from '../../../services/auth';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../../../firebase/config';
import { updateProfile } from 'firebase/auth';
import { getClientBookings } from '../../../services/firestore/bookings';
import { uploadImage } from '../../../services/storage';
import { ArrowRight, User, Mail, Calendar, MapPin, Package, Upload, X, Camera } from 'lucide-react';

const ClientProfile = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [createdAt, setCreatedAt] = useState('');
  const [bookings, setBookings] = useState([]);
  const [photoURL, setPhotoURL] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const loadProfile = async () => {
      if (!user) return;
      try {
        const [userData, bookingsData] = await Promise.all([
          getUserData(user.uid),
          getClientBookings(user.uid).catch(() => [])
        ]);
        
        if (userData) {
          setDisplayName(userData.displayName || '');
          setEmail(userData.email || user.email || '');
          setPhotoURL(userData.photoURL || user.photoURL || '');
          if (userData.createdAt) {
            const date = userData.createdAt.toDate ? userData.createdAt.toDate() : new Date(userData.createdAt);
            setCreatedAt(date.toLocaleDateString());
          }
        }
        setBookings(bookingsData || []);
      } catch (error) {
        console.error('Error loading profile:', error);
      } finally {
        setLoading(false);
      }
    };
    loadProfile();
  }, [user]);

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    setPhotoFile(file);
    setError('');

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPhotoPreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const removePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);
    setUploadingPhoto(false);

    try {
      if (!user) {
        throw new Error('User not authenticated');
      }

      let newPhotoURL = photoURL;

      // Upload new photo if selected
      if (photoFile) {
        setUploadingPhoto(true);
        try {
          const path = `profiles/${user.uid}/${Date.now()}_${photoFile.name}`;
          newPhotoURL = await uploadImage(photoFile, path);
          setPhotoURL(newPhotoURL);
          setPhotoFile(null);
          setPhotoPreview(null);
        } catch (uploadError) {
          setError('Failed to upload photo. Please try again.');
          setUploadingPhoto(false);
          setSaving(false);
          return;
        }
        setUploadingPhoto(false);
      }

      // Update Firestore
      await updateDoc(doc(db, 'users', user.uid), {
        displayName,
        photoURL: newPhotoURL,
        updatedAt: serverTimestamp(),
      });

      // Update Firebase Auth profile
      if (auth.currentUser) {
        await updateProfile(auth.currentUser, {
          displayName,
          photoURL: newPhotoURL,
        });
      }

      setSuccess('Profile updated successfully');
    } catch (err) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setSaving(false);
      setUploadingPhoto(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-emerald-50/20 via-white to-green-50/10">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-emerald-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50/20 via-white to-green-50/10 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-light text-gray-900 mb-2">My Profile</h1>
          <p className="text-lg text-gray-600 font-light">
            Manage your account information
          </p>
        </div>

        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 p-8 sm:p-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="rounded-lg bg-red-50 border-2 border-red-300 p-4">
                <p className="text-sm font-medium text-red-800">{error}</p>
              </div>
            )}

            {success && (
              <div className="rounded-lg bg-emerald-50 border-2 border-emerald-300 p-4">
                <p className="text-sm font-medium text-emerald-800">{success}</p>
              </div>
            )}

            {/* Profile Picture */}
            <div className="flex flex-col items-center mb-6 pb-6 border-b border-gray-200">
              <div className="relative group">
                <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-emerald-100 bg-gray-100">
                  {photoPreview || photoURL ? (
                    <img
                      src={photoPreview || photoURL}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-emerald-100 to-green-100">
                      <User className="w-16 h-16 text-emerald-600/50" />
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute bottom-0 right-0 p-3 bg-emerald-600 text-white rounded-full shadow-lg hover:bg-emerald-700 transition-colors group-hover:scale-110 transition-transform"
                  title="Change profile picture"
                >
                  <Camera className="w-5 h-5" />
                </button>
                {photoPreview && (
                  <button
                    type="button"
                    onClick={removePhoto}
                    className="absolute top-0 right-0 p-2 bg-red-500 text-white rounded-full shadow-lg hover:bg-red-600 transition-colors"
                    title="Remove photo"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handlePhotoSelect}
              />
              <p className="text-xs text-gray-500 mt-2 text-center">
                Click the camera icon to upload a profile picture
                <br />
                Max size: 5MB
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label htmlFor="displayName" className="block text-sm font-medium text-gray-700 mb-2">
                  <User className="w-4 h-4 inline mr-2" />
                  Full Name
                </label>
                <input
                  id="displayName"
                  type="text"
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 bg-white/50"
                  placeholder="John Doe"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  <Mail className="w-4 h-4 inline mr-2" />
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  disabled
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-500 bg-gray-50 cursor-not-allowed"
                  value={email}
                />
                <p className="mt-1 text-xs text-gray-500">Email cannot be changed</p>
              </div>

              {createdAt && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Calendar className="w-4 h-4 inline mr-2" />
                    Member Since
                  </label>
                  <div className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-700 bg-gray-50">
                    {createdAt}
                  </div>
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={saving || uploadingPhoto}
              className="w-full group bg-emerald-700 hover:bg-emerald-800 text-white px-6 py-4 rounded-lg text-base font-medium shadow-lg shadow-emerald-200/50 hover:shadow-xl hover:shadow-emerald-300/50 transition-all duration-300 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving || uploadingPhoto ? (
                uploadingPhoto ? 'Uploading photo...' : 'Saving...'
              ) : (
                <>
                  Save Changes
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
          </form>
        </div>

        {/* My Trips Section */}
        <div className="mt-8 bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 p-8 sm:p-10">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-light text-gray-900 mb-1 flex items-center gap-2">
                <MapPin className="w-6 h-6" />
                My Trips
              </h2>
              <p className="text-sm text-gray-600">Your booked trips and experiences</p>
            </div>
            <Link
              to="/client/trips"
              className="text-emerald-700 hover:text-emerald-800 text-sm font-medium flex items-center gap-1"
            >
              Browse More
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {bookings.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">You haven't booked any trips yet.</p>
              <Link
                to="/client/trips"
                className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-700 text-white rounded-lg hover:bg-emerald-800 transition-colors text-sm font-medium"
              >
                Explore
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {bookings.slice(0, 4).map((booking) => (
                <Link
                  key={booking.id}
                  to={`/client/bookings/${booking.id}`}
                  className="block bg-gradient-to-br from-emerald-50/50 to-green-50/30 rounded-lg border border-emerald-100 p-4 hover:shadow-md transition-all hover:border-emerald-200"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <h3 className="text-base font-medium text-gray-900 mb-1">
                        Booking #{booking.id?.substring(0, 8)}
                      </h3>
                      {booking.tripTitle && (
                        <p className="text-sm text-gray-600 mb-2">{booking.tripTitle}</p>
                      )}
                      {booking.totalAmount && (
                        <p className="text-sm font-medium text-emerald-700">
                          ${booking.totalAmount}
                        </p>
                      )}
                    </div>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        booking.status === 'confirmed'
                          ? 'bg-emerald-100 text-emerald-800'
                          : booking.status === 'pending'
                          ? 'bg-amber-100 text-amber-800'
                          : booking.status === 'canceled'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {booking.status}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
          {bookings.length > 4 && (
            <div className="mt-4 text-center">
              <Link
                to="/client/bookings"
                className="text-emerald-700 hover:text-emerald-800 text-sm font-medium"
              >
                View all {bookings.length} bookings →
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientProfile;

